<script setup lang="ts">
import { useRouter } from "vue-router";
import { BCheckbox, BField, BButton } from "buefy";
import TitleCardWithLimitLength from "@/components/common/TitleCardWithLimitLength.vue";
import { GetAuthorizationResult } from "@/types/generated/UsedGeneratedTypes";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";

interface Row {
  name: string;
}

interface Props {
  resultFilterAuthByName: GetAuthorizationResult[];
  checkbox: Record<string, boolean>;
  row: Row;
  listColumnName: Record<string, unknown>;
  isUserManager: boolean;
  updateListUsersInAuthorization: (auth: GetAuthorizationResult, rowName: string) => void;
  lang: string;
  application: LocalApplicationResult;
  /**
   * Force le composant en mode lecture seule (cases grisées et non
   * cliquables). Utilisé par exemple lorsque la demande est refusée :
   * la sélection d'autorisations n'a alors plus de sens.
   */
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), { disabled: false });

const router = useRouter();

/**
 * Click sur l'icône oeil : redirige vers la page de gestion des
 * autorisations de l'application en pré-remplissant le filtre par nom
 * pour ne montrer que cette autorisation. Remplace l'ancienne modale
 * de détail (jugée peu lisible).
 */
function viewAuthorization(auth: GetAuthorizationResult) {
  const appName = props.application?.name;
  if (!appName) return;
  router.push({
    path: `/applications/${appName}/authorizations`,
    query: { q: auth.name },
  });
}
</script>

<template>
  <div class="columns is-multiline">
    <b-field
      v-for="auth in props.resultFilterAuthByName"
      :key="auth.uuid"
      class="column is-12"
    >
      <b-checkbox
        v-model="props.checkbox[auth.name + '_' + props.row.name]"
        :aria-label="auth.uuid + ': ' + props.checkbox[auth.name + '_' + props.row.name]"
        :disabled="!props.isUserManager || props.disabled"
        @update:model-value="props.updateListUsersInAuthorization(auth, props.row.name)"
      >
        <TitleCardWithLimitLength
          :limit="35"
          :local-name="auth.name"
          position="is-left"
        />
      </b-checkbox>

      <b-button
        class="btnShowAuth"
        icon-left="eye"
        outlined
        size="is-small"
        type="is-dark"
        :aria-label="$t('dataTypeAuthorizations.showMore')"
        @click="viewAuthorization(auth)"
      />
    </b-field>
  </div>
</template>
